export type TimetableDayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';

export interface TimetablePeriodEntry {
  period: number;
  time?: string;
  monday?: string;
  tuesday?: string;
  wednesday?: string;
  thursday?: string;
  friday?: string;
  [key: string]: string | number | undefined;
}

export type CurrentTimetableStatus =
  | { kind: 'period'; period: number; subject: string }
  | { kind: 'lunch' }
  | null;

const DAY_KEYS: TimetableDayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

/** 다음 교시 시작 시각이 없을 때, 또는 점심 직전 오전 교시 길이(분) */
const DEFAULT_PERIOD_MINUTES = 40;

/** time 필드가 비었을 때 쓰는 기본 시작 시각 */
const FALLBACK_START_TIMES = ['09:00', '10:00', '11:00', '11:30', '13:20', '14:10', '15:00'];

function parseTimeToMinutes(time: string | undefined): number | null {
  if (!time || typeof time !== 'string') return null;
  const match = time.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function getEntryStartMinutes(entry: TimetablePeriodEntry): number {
  return (
    parseTimeToMinutes(entry.time) ??
    parseTimeToMinutes(FALLBACK_START_TIMES[entry.period - 1]) ??
    9 * 60 + (entry.period - 1) * 50
  );
}

/**
 * 현재 교시/점심 여부.
 * 시간표 entry.time(교시 시작)을 기준으로 판정한다.
 * 4교시~5교시 사이 공백은 UI와 같이 점심으로 본다.
 */
export function getCurrentTimetableStatus(
  entries: TimetablePeriodEntry[],
  now: Date = new Date()
): CurrentTimetableStatus {
  const dayOfWeek = now.getDay(); // 0=일요일
  if (dayOfWeek === 0 || dayOfWeek === 6) return null;

  const dayKey = DAY_KEYS[dayOfWeek - 1];
  if (!dayKey) return null;

  const sorted = [...entries]
    .filter((entry) => entry && Number.isFinite(entry.period))
    .sort((a, b) => a.period - b.period || getEntryStartMinutes(a) - getEntryStartMinutes(b));

  if (sorted.length === 0) return null;

  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const asPeriod = (entry: TimetablePeriodEntry): CurrentTimetableStatus => ({
    kind: 'period',
    period: entry.period,
    subject: String(entry[dayKey] ?? '-'),
  });

  type Slot =
    | { kind: 'period'; entry: TimetablePeriodEntry; start: number; end: number }
    | { kind: 'lunch'; start: number; end: number };

  const slots: Slot[] = [];

  for (let i = 0; i < sorted.length; i++) {
    const entry = sorted[i];
    const start = getEntryStartMinutes(entry);
    const next = sorted[i + 1];
    const nextStart = next ? getEntryStartMinutes(next) : start + DEFAULT_PERIOD_MINUTES;

    // 오전 마지막(≤4) → 오후 첫(≥5) 사이는 점심
    if (entry.period <= 4 && next && next.period >= 5) {
      const periodEnd = Math.min(start + DEFAULT_PERIOD_MINUTES, nextStart);
      if (periodEnd > start) {
        slots.push({ kind: 'period', entry, start, end: periodEnd });
      }
      if (nextStart > periodEnd) {
        slots.push({ kind: 'lunch', start: periodEnd, end: nextStart });
      }
      continue;
    }

    slots.push({
      kind: 'period',
      entry,
      start,
      end: Math.max(start + 1, nextStart),
    });
  }

  for (const slot of slots) {
    if (nowMinutes >= slot.start && nowMinutes < slot.end) {
      return slot.kind === 'lunch' ? { kind: 'lunch' } : asPeriod(slot.entry);
    }
  }

  return null;
}
